import * as RadixDialog from '@radix-ui/react-dialog';
import { cva, cx, VariantProps } from 'class-variance-authority';
import React from 'react';
import { Icon } from '@iconify/react';
import { twMerge } from 'tailwind-merge';
export type DialogRootProps = RadixDialog.DialogProps;
const Root = RadixDialog.Root;

export type DialogTriggerProps = RadixDialog.DialogTriggerProps;
const Trigger = RadixDialog.Trigger;

export type DialogDescriptionProps = RadixDialog.DialogDescriptionProps;
const Description = RadixDialog.Description;

export type DialogContentProps = RadixDialog.DialogContentProps &
  VariantProps<typeof dialogStyles>;
function Content(props: DialogContentProps) {
  const { className, children, variant, fullscreen, scrollable, ...rest } =
    props;
  return (
    <RadixDialog.Portal>
      <RadixDialog.Overlay className={twMerge(overlayStyles({ variant }))} />
      <RadixDialog.Content
        {...rest}
        className={cx(
          twMerge(dialogStyles({ variant, fullscreen, scrollable }), className)
        )}
      >
        {children}
      </RadixDialog.Content>
    </RadixDialog.Portal>
  );
}

const overlayStyles = cva('fixed inset-0 z-20', {
  variants: {
    variant: {
      transparent: 'bg-transparent',
      unstyled: 'bg-black-100/50',
      default: 'bg-black-100/50',
      light: 'bg-black-100/50',
      vault: 'bg-black-100/50',
      dark: 'bg-[#000000]/95',
      darkSquare: 'bg-[#000000]/50',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const dialogStyles = cva(
  'fixed flex flex-col animate-dialog top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 z-20',
  {
    variants: {
      fullscreen: {
        true: 'w-screen max-h-screen',
        false: 'min-w-[320px] max-w-[450px]',
      },
      scrollable: {
        true: 'overflow-y-auto pb-36 h-full max-h-full pt-12 scrollbar-hide',
        false: '',
      },
      variant: {
        transparent: '',
        unstyled: '',
        default: 'bg-blue-700 p-6 rounded shadow-card',
        light: 'bg-white p-6 rounded shadow-card',
        vault:
          'bg-light-modalBackgroundColor dark:bg-dark-modalBackgroundColor rounded shadow-card',
        dark: 'rounded-lg border-[0.6px] bg-black-900 border-black-700 p-[1.875rem]',
        darkSquare: 'rounded-none border-[0.6px] bg-black border-white p-7',
      },
    },
    defaultVariants: {
      variant: 'default',
      fullscreen: false,
    },
  }
);

export type DialogBodyProps = React.ComponentProps<'div'> &
  VariantProps<typeof bodyStyles>;
function Body(props: DialogBodyProps) {
  const { className, variant, ...rest } = props;
  return (
    <div className={twMerge(bodyStyles({ variant }), className)} {...rest} />
  );
}

const bodyStyles = cva('', {
  variants: {
    variant: {
      default: '-mx-6 max-h-full px-6',
      unstyled: '',
      dark: '',
      darkSquare: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

type BodyTitleProps = React.ComponentProps<'div'> & {
  endEnhancer?: React.ReactNode;
} & VariantProps<typeof bodyTitleStyles>;

function BodyTitle({
  className,
  children,
  variant,
  endEnhancer,
  ...rest
}: BodyTitleProps) {
  return (
    <RadixDialog.Title asChild {...rest}>
      <div className={twMerge(bodyTitleStyles({ variant }), className)}>
        {children}
        {endEnhancer ? <div>{endEnhancer}</div> : null}
      </div>
    </RadixDialog.Title>
  );
}

const bodyTitleStyles = cva('', {
  variants: {
    variant: {
      default: '',
      unstyled: '',
      dark: 'flex relative flex-row justify-between items-center z-0',
      darkSquare: 'flex relative flex-row justify-between items-center z-0',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export type DialogTitleProps = RadixDialog.DialogTitleProps &
  VariantProps<typeof titleStyle>;
const Title = React.forwardRef<HTMLHeadingElement, DialogTitleProps>(
  function Title(props, ref) {
    const { className, variant, ...rest } = props;
    return (
      <RadixDialog.Title
        ref={ref}
        className={cx(titleStyle({ variant }), className)}
        {...rest}
      />
    );
  }
);
const titleStyle = cva('', {
  variants: {
    variant: {
      default: 'text-xl font-medium text-white',
      unstyled: '',
      dark: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export type DialogCloseProps = RadixDialog.DialogCloseProps &
  VariantProps<typeof closeStyle>;
const Close = React.forwardRef<HTMLButtonElement, DialogCloseProps>(
  function Close(props, ref) {
    const {
      className,
      children = (
        <Icon
          icon='eva:close-outline'
          className='text-white'
          width='24'
          height='24'
        />
      ),
      variant,
      ...rest
    } = props;
    return (
      <RadixDialog.Close
        ref={ref}
        asChild
        className={cx(closeStyle({ variant }), className)}
        {...rest}
      >
        {children}
      </RadixDialog.Close>
    );
  }
);
const closeStyle = cva('', {
  variants: {
    variant: {
      default: 'absolute top-3 right-3 hover:cursor-pointer',
      unstyled: '',
      dark: 'hover:cursor-pointer',
      darkSquare: 'hover:cursor-pointer',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export const ModalV2 = {
  Root,
  Trigger,
  Title,
  Content,
  Body,
  BodyTitle,
  Close,
  Description,
};
